/**
 * useGptInpaint — GPT 局部重绘 Hook
 *
 * ── 功能概述 ──
 * 为画布上所有非 Midjourney 的已完成图片节点提供"局部重绘"能力。
 * 后端支持多渠道（云雾 + ToAPIs），前端无需感知具体走哪个渠道。
 *
 * ── 交互流程 ──
 *   1. 用户选中图片节点 → 节点上方出现亮蓝色 GptInpaintToolbar 胶囊
 *   2. 点击"局部重绘" → 打开 InpaintMaskEditor（maskFormat='openai'）
 *   3. 用户涂抹遮罩 + 输入提示词 → 提交
 *   4. 本 Hook 在源节点右侧创建 loading 新节点 + 连线
 *   5. POST /api/gpt-inpaint → 后端异步生图（多渠道 fallback）
 *   6. 通过 WebSocket node-updated / node-completed 推送结果到前端
 *
 * ── 新节点创建要点 ──
 *   generationModelLogo / generationModelName:
 *     优先从 callbacksRef.gptInpaintConfigRef（工具箱配置）读取，
 *     回退到源节点的值。确保生成中状态显示管理员配置的图标和名称。
 *   customSize:
 *     继承源节点的 customSize，保证节点大小一致。
 *
 * ── 遮罩格式（重要）──
 * OpenAI edits 接口的 mask 规范与 MJ 不同：
 *   - OpenAI: 透明(alpha=0) = 要重绘的区域，不透明(alpha=255) = 保留
 *   - MJ:     白色 = 要重绘，黑色 = 保留
 * InpaintMaskEditor 通过 maskFormat='openai' prop 自动处理转换。
 *
 * ── 关联文件 ──
 *   工具条:       node-parts/GptInpaintToolbar.js
 *   遮罩编辑器:   InpaintMaskEditor.js
 *   后端路由:     server/routes/ai.js → POST /api/gpt-inpaint（多渠道 fallback）
 *   Provider(云雾): server/providers/yunwu-gptimage2.js（gpt_inpaint）
 *   Provider(ToAPIs): server/providers/toapis-gptimage2.js（gpt_inpaint_toapis）
 *   接入点:       EditorPage.js（导入 Hook + 渲染 InpaintMaskEditor）
 *   节点显示:     CustomNode.js（showGptInpaintToolbar 条件判断）
 */
import { useCallback, useState } from 'react';
import { useReactFlow } from 'reactflow';
import * as api from '../api';
import * as C from '../constants';

export default function useGptInpaint({ setNodes, setEdges, projectId, setAlertMsg, callbacksRef }) {
  const { getNodes } = useReactFlow();

  // ── Inpaint 遮罩编辑器状态 ──
  const [gptInpaintState, setGptInpaintState] = useState(null); // { sourceNodeId, imageUrl }

  const handleGptInpaint = useCallback((nodeId) => {
    const sourceNode = getNodes().find(n => n.id === nodeId);
    if (!sourceNode) {
      setAlertMsg?.({ type: 'error', text: '源节点不存在' });
      return;
    }
    const imageUrl = sourceNode.data?.localCacheUrl || sourceNode.data?.output;
    if (!imageUrl) {
      setAlertMsg?.({ type: 'error', text: '源节点没有图片' });
      return;
    }
    setGptInpaintState({ sourceNodeId: nodeId, imageUrl });
  }, [getNodes, setAlertMsg]);

  // ── Inpaint 提交回调（由 InpaintMaskEditor 调用）──
  const handleGptInpaintSubmit = useCallback(async ({ maskBase64, prompt, maskMeta }) => {
    if (!gptInpaintState) return;
    const { sourceNodeId } = gptInpaintState;

    const allNodes = getNodes();
    const sourceNode = allNodes.find(n => n.id === sourceNodeId);
    if (!sourceNode) {
      setAlertMsg?.({ type: 'error', text: '源节点不存在' });
      return;
    }

    const targetId = `node-${Date.now()}-gpt-inpaint`;
    const sourcePos = sourceNode.position;
    const sourceWidth = sourceNode.data?.customSize?.width || 240;

    // 从工具箱配置中获取 logo 和名称（callbacksRef 挂载了 gptInpaintConfigRef）
    const gptConfig = callbacksRef?.current?.gptInpaintConfigRef?.current || {};
    const toolboxLogo = gptConfig.logo || sourceNode.data?.generationModelLogo || '';
    const toolboxName = gptConfig.name || 'GPT 局部重绘';

    const newNode = {
      id: targetId,
      type: 'aiNode',
      position: { x: sourcePos.x + sourceWidth + 60, y: sourcePos.y },
      selected: false,
      data: {
        type: 'image',
        title: '局部重绘',
        loading: true,
        generationStartTime: Date.now(),
        generationModelName: toolboxName,
        generationModelLogo: toolboxLogo,
        statusInfo: { status: '局部重绘处理中...' },
        isDetached: true,
        isDirectUpload: true,
        customSize: sourceNode.data?.customSize || { width: 240, height: 240 },
        onUpdate: callbacksRef?.current?.onUpdate,
        onDelete: callbacksRef?.current?.onDelete,
        onGenerate: callbacksRef?.current?.onGenerate,
        onReupload: callbacksRef?.current?.onReupload,
        onReplace: callbacksRef?.current?.onReplace,
        onDetach: callbacksRef?.current?.onDetach,
        onRemoveRefFile: callbacksRef?.current?.onRemoveRefFile,
        onRemoveRefText: callbacksRef?.current?.onRemoveRefText,
        onRemoveRefAudio: callbacksRef?.current?.onRemoveRefAudio,
        onHandleDoubleClick: callbacksRef?.current?.onHandleDoubleClick,
        onDescribe: callbacksRef?.current?.onDescribe,
        onDescribeComplete: callbacksRef?.current?.onDescribeComplete,
        getMjDescribeConfig: callbacksRef?.current?.getMjDescribeConfig,
        globalAssetsRef: callbacksRef?.current?.globalAssetsRef,
        publicAssetsRef: callbacksRef?.current?.publicAssetsRef,
      }
    };

    const newEdge = {
      id: `edge-gpt-inpaint-${sourceNodeId}-${targetId}`,
      source: sourceNodeId,
      target: targetId,
      type: 'custom',
      data: { locked: true, originalColor: C.EDGE_COLOR_AUTO, isTransmitting: true },
      style: { stroke: C.EDGE_COLOR_AUTO, strokeWidth: C.EDGE_WIDTH_NORMAL, opacity: C.OPACITY_IDLE },
    };

    setNodes(nds => [...nds, newNode]);
    setEdges(eds => [...eds, newEdge]);

    try {
      await api.apiPost('/api/gpt-inpaint', {
        projectId,
        sourceNodeId,
        targetNodeId: targetId,
        maskBase64: maskBase64 || undefined,
        prompt: prompt || undefined,
      });
    } catch (err) {
      setNodes(nds => nds.map(n => {
        if (n.id === targetId) {
          return { ...n, data: { ...n.data, loading: false, error: err.message || 'GPT 局部重绘请求失败' } };
        }
        return n;
      }));
      setAlertMsg?.({ type: 'error', text: `GPT 局部重绘失败: ${err.message}` });
    }
  }, [gptInpaintState, getNodes, setNodes, setEdges, projectId, setAlertMsg, callbacksRef]);

  const closeGptInpaintEditor = useCallback(() => setGptInpaintState(null), []);

  return { handleGptInpaint, gptInpaintState, handleGptInpaintSubmit, closeGptInpaintEditor };
}
